export const FINES_MATERIAL_KEY = "fines";
export const FINES_CHART_COLOR = "#FFA500";
export const DEFAULT_FINES_THRESHOLD_CM = 40;

/**
 * Same rule as characterization historyMaterials: an object counts as fines
 * only when longestSideMeters is a finite number strictly below the threshold
 * (meters). Missing size stays in its material bucket.
 */
export const isObjectBelowFinesThreshold = (object, thresholdMeters) => {
  if (thresholdMeters == null || !(thresholdMeters > 0)) return false;
  const longestSide = Number(
    object?.subregion?.longestSideMeters ?? object?.longestSideMeters,
  );
  if (!Number.isFinite(longestSide)) return false;
  return longestSide < thresholdMeters;
};

/**
 * Split per-material object lists into zoom-safe objects (>= threshold or
 * unknown size) and a fines count (objects strictly below threshold).
 */
export const splitObjectsByFinesThreshold = (
  objectsByMaterial,
  thresholdCm,
) => {
  const thresholdMeters =
    thresholdCm != null && Number(thresholdCm) > 0
      ? Number(thresholdCm) / 100
      : null;

  const zoomObjectsByMaterial = {};
  let finesCount = 0;

  Object.entries(objectsByMaterial || {}).forEach(([materialKey, objects]) => {
    const kept = [];
    (objects || []).forEach((object) => {
      if (isObjectBelowFinesThreshold(object, thresholdMeters)) {
        finesCount += 1;
      } else {
        kept.push(object);
      }
    });
    if (kept.length > 0) {
      zoomObjectsByMaterial[materialKey] = kept;
    }
  });

  return { zoomObjectsByMaterial, finesCount };
};

/**
 * Build the materials distribution chart entries: one bar per material for
 * objects at/above the fines threshold, plus an optional orange "Fines" bar.
 * Zoom thumbnails use zoomObjectsByMaterial (no fines crops).
 */
export const buildMaterialsWithFines = ({
  objectsByMaterial,
  thresholdCm = DEFAULT_FINES_THRESHOLD_CM,
  translateLabel,
  colorForMaterial,
  apiFallbackMaterials = [],
}) => {
  const hasObjects = Object.keys(objectsByMaterial || {}).length > 0;
  if (!hasObjects) {
    const materials = apiFallbackMaterials || [];
    return {
      materials,
      zoomObjectsByMaterial: {},
      resolvedTotal: materials.reduce(
        (sum, material) => sum + (Number(material.count) || 0),
        0,
      ),
    };
  }

  const { zoomObjectsByMaterial, finesCount } = splitObjectsByFinesThreshold(
    objectsByMaterial,
    thresholdCm,
  );

  const materials = Object.entries(zoomObjectsByMaterial)
    .map(([value, objects]) => ({
      value,
      label: translateLabel(value),
      color: colorForMaterial(value),
      count: objects.length,
    }))
    .filter((material) => material.count > 0);

  if (finesCount > 0) {
    const threshold = Number(thresholdCm);
    materials.push({
      value: FINES_MATERIAL_KEY,
      label:
        Number.isFinite(threshold) && threshold > 0
          ? translateLabel("Fines (below {{threshold}} cm)", {
              threshold,
            })
          : translateLabel("Fines"),
      color: FINES_CHART_COLOR,
      count: finesCount,
    });
  }

  materials.sort((a, b) => b.count - a.count);

  return {
    materials,
    zoomObjectsByMaterial,
    resolvedTotal: materials.reduce(
      (sum, material) => sum + (Number(material.count) || 0),
      0,
    ),
  };
};
